import { reset, sizeInPxToFontSize, unit } from "./util";

import { css } from "styled-components";

export const spacing = {
  standard: 16,
};
Object.assign(spacing, {
  // Real-life values
  padding: unit(spacing.standard, "px"),
  paddingHalf: unit(spacing.standard / 2, "px"),
  paddingDouble: unit(spacing.standard * 2, "px"),
});

export const color = {
  blue: "#141B4D",
  darkBlue: "#1C3775",
  mediumBlue: "#98B2DD",
  lightBlue: "#C1D1EB",
  darkGrey: "#020202",
  orange: "#FC6100",
  grey: "#555554",
  lightGrey: "#ebebeb",
  white: "#ffffff",
};
Object.assign(color, {
  // Real-life values
  primary: color.blue,
  secondary: color.orange,
  text: color.blue,
  title: color.darkBlue,
  background: color.white,
  success: "#15a575",
  warning: "#eda715",
  error: "#be0a28",
});
Object.assign(color, {
  // Sub-values
  collection: { background: color.lightGrey },
  configurator: { attribute: { secondary: color.darkBlue } },
  line: color.darkBlue,
  button: { normal: color.primary, hover: color.mediumBlue },
  slider: { arrow: color.darkBlue },
  header: { background: color.background },
  link: { normal: color.primary, hover: color.mediumBlue },
});

export const transition = {
  standard: {
    duration: "0.25s",
    mode: "ease",
  },
};
Object.assign(transition, {
  // Real-life values
  default: `${transition.standard.duration} ${transition.standard.mode}`,
  link: `${transition.standard.duration} ${transition.standard.mode}`,
});

export const fontFamily = {
  default: ["Roboto", "Verdana", "sans serif"].join(","),
  headline: ["Oswald", "Verdana", "sans serif"].join(", "),
};
export const fontWeight = {
  default: {
    light: 300,
    normal: 400,
    bold: 700,
  },
  headline: {
    light: 300,
    normal: 400,
    bold: 700,
  },
};
export const font = {
  default(size = null, lineHeight = 1.3) {
    return css`
      font: ${fontWeight.default.regular}
        ${size ? sizeInPxToFontSize(size) : "1em"} /
        ${size && typeof lineHeight === "number"
          ? sizeInPxToFontSize(size * lineHeight)
          : unit(lineHeight, "em")}
        ${fontFamily.default};
    `;
  },
  bold(size = null, lineHeight = 1.3) {
    return css`
      font: ${fontWeight.default.bold}
        ${size ? sizeInPxToFontSize(size) : "1em"} /
        ${size && typeof lineHeight === "number"
          ? sizeInPxToFontSize(size * lineHeight)
          : unit(lineHeight, "em")}
        ${fontFamily.default};
    `;
  },
  italic(size = null, lineHeight = 1.3) {
    return css`
      font: ${fontWeight.default.regular} italic
        ${size ? sizeInPxToFontSize(size) : "1em"} /
        ${size && typeof lineHeight === "number"
          ? sizeInPxToFontSize(size * lineHeight)
          : unit(lineHeight, "em")}
        ${fontFamily.default};
    `;
  },
  boldItalic(size = null, lineHeight = 1.3) {
    return css`
      font: ${fontWeight.default.bold} italic
        ${size ? sizeInPxToFontSize(size) : "1em"} /
        ${size && typeof lineHeight === "number"
          ? sizeInPxToFontSize(size * lineHeight)
          : unit(lineHeight, "em")}
        ${fontFamily.default};
    `;
  },
  title(size = null, lineHeight = 1.3) {
    return css`
      font: ${fontWeight.headline.bold}
        ${size ? sizeInPxToFontSize(size) : "1.8em"} /
        ${size && typeof lineHeight === "number"
          ? sizeInPxToFontSize(size * lineHeight)
          : unit(lineHeight, "em")}
        ${fontFamily.headline};
    `;
  },
  subtitle(size = null, lineHeight = 1.3) {
    return css`
      font: ${fontWeight.headline.regular}
        ${size ? sizeInPxToFontSize(size) : "1.2em"} /
        ${size && typeof lineHeight === "number"
          ? sizeInPxToFontSize(size * lineHeight)
          : unit(lineHeight, "em")}
        ${fontFamily.headline};
    `;
  },
};
Object.assign(font, {
  // Real-life values
  headline: css`
    ${font.title(24)};
    text-transform: uppercase;
  `,
  subheadline: css`
    ${font.subtitle(16)};
    text-transform: uppercase;
  `,
  button: css`
    ${font.title(14, "20px")};
    text-transform: uppercase;
  `,

  home: {
    headline: css`
      ${font.subtitle(20)};
      text-transform: uppercase;
    `,
    slider: font.title(18, "20px"),
    title: font.title(20, "20px"),
  },
  static: {
    title: css`
      ${font.title(32, 1)};
      text-transform: uppercase;
    `,
    subtitle: css`
      ${font.subtitle(24, 1)};
      text-transform: uppercase;
    `,
    baseline: css`
      ${font.default(22)};
      text-transform: uppercase;
    `,
    strong: css`
      ${font.bold(14)};
      text-transform: uppercase;
    `,
    text: font.default(14, 1.4),
    kpititle: css`
      ${font.default(24, 1)};
      text-transform: uppercase;
    `,
    kpifigure: css`
      ${font.default(24, 1.2)};
    `,
    kpitext: css`
      ${font.default(14, 1.2)};
    `,
  },
  mainMenu: {
    firstLevel: css`
      ${font.subtitle(16)};
      text-transform: uppercase;
    `,
    secondLevel: css`
      ${font.bold(16)};
      text-transform: none;
    `,
  },
  menu: {
    bold: css`
      ${font.title(16)};
      text-transform: uppercase;
    `,
    normal: css`
      ${font.subtitle(16)};
      text-transform: uppercase;
    `,
    active: css`
      ${font.title(16)};
      text-transform: uppercase;
    `,
  },
  configurator: {
    part: css`
      ${font.subtitle(15)};
      text-transform: uppercase;
    `,
    topmenu: {
      normal: css`
        ${font.subtitle(16)};
        text-transform: uppercase;
      `,
      active: css`
        ${font.title(16)};
        text-transform: uppercase;
      `,
    },
    product: {
      normal: css`
        ${font.subtitle(16)};
      `,
      active: css`
        ${font.title(16)};
        color: ${color.secondary};
      `,
    },
    attributes: {
      headline: css`
        ${font.italic(14, 1)};
        text-transform: uppercase;
      `,
      subheadline: css`
        ${font.boldItalic(14, 1)};
        text-transform: none;
      `,
      tag: css`
        ${font.italic(12, 1.5)};
      `,
    },
    message: css`
      ${font.default(14)};
    `,
    reference: css`
      ${font.default(10)};
      text-transform: uppercase;
    `,
  },
  cart: {
    action: css`
      ${font.title(14)};
      text-transform: uppercase;
    `,
    item: {
      collection: css`
        ${font.title(16)};
      `,
      product: css`
        ${font.subtitle(16)};
      `,
    },
  },
});

export const style = {
  link: css`
    ${reset()};
    color: ${color.link.normal};
    text-decoration: none;
    transition: color ${transition.link};

    &:hover {
      color: ${color.link.hover};
    }
  `,
  button: css`
    ${reset()};
    ${font.button};
    background: transparent none;
    border: 2px solid ${color.button.normal};
    border-radius: 0;
    color: ${color.button.normal};
    cursor: pointer;
    padding: ${spacing.padding} ${spacing.paddingDouble};
    text-align: center;
    text-decoration: none;
    transition: all ${transition.default};

    &:hover {
      border-color: ${color.button.hover};
      color: ${color.button.hover};
    }
  `,
  menu: css`
    ${reset()};
    color: ${color.link.normal};
    text-decoration: none;
    transition: color ${transition.link};

    &:hover {
      color: ${color.link.hover};
    }
  `,
  line: {
    thin: css`1px solid ${color.primary}`,
    bold: css`3px solid ${color.primary}`,
  },
};
